<template>
  <div id="app">
    <div class="app-container d-flex justify-content-center" v-if="this.$cookies.get('access_token') !== null">
      <transition name="fade" mode="out-in">
        <main class="w-100">
          <div class="header-container d-flex justify-content-between position-sticky">
            <div class="header-left">
              <img class="logo pl-4 pt-3" alt="Feeds.automated logo" src="/app/images/logo.svg" />
            </div>
            <div class="header-right d-flex align-items-center">
              <div class="search-container d-flex mr-4">
                <input type="text" class="form-control" name="searchField" :placeholder="$t('global.search')">
                <button type="button" class="btn btn-tiny btn-orange"><search-icon></search-icon></button>
              </div>
              <div class="user-avatar d-flex align-items-center justify-content-center mr-4">{{ userName.substr(0,1) + userSurname.substr(0,1) }}</div>
              <div class="user-name">{{ userName }} {{ userSurname }}</div>
            </div>
          </div>
          <div class="body-container d-flex w-100 justify-content-center">
            <sidebar-menu :menu="menu" :hideToggle="true" />
            <div class="w-100" v-if="!isLoading">
              <transition name="fade" mode="out-in">
                <router-view :key="$route.path" @onProjectSelect="selectProject" @onProjectCreate="createProject" :projects="projects" :project="selectedProject"></router-view>
              </transition>
            </div>
            <div v-else>
              <div class="spinner-border text-orange" role="status"></div>
            </div>
          </div>
        </main>
      </transition>
    </div>
    <div class="app-container d-flex justify-content-center" v-else>
      <transition name="fade" mode="out-in">
        <router-view :key="$route.path" @onAccountActivate="activateAccount" @onLoginSuccess="getProjects" ></router-view>
      </transition>
    </div>
  </div>
</template>

<script>
import jwtDecode from 'jwt-decode'
import { EventBus } from './event-bus.js'
import axios from 'axios'
import { SearchIcon } from 'vue-feather-icons'
import init from '@/mixins/init'

export default {
  name: 'App',

  data () {
    return {
      cookieLanguage: this.$cookies.get('language_version'),
      cookieToken: this.$cookies.get('access_token'),
      menu: [],
      planLevel: '',
      projects: [],
      userName: '',
      userSurname: ''
    }
  },

  mixins: [init],

  mounted () {
    if (this.cookieLanguage !== null) this.$i18n.locale = this.cookieLanguage
    else {
      this.$i18n.locale = navigator.language || navigator.userLanguage
      this.$cookies.set('language_version', navigator.language.substr(0, 2) || navigator.userLanguage.substr(0, 2))
    }
    if (this.cookieToken !== null) {
      this.updateUserData(this.cookieToken)
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/auth/refresh')
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.$cookies.set('access_token', response.data.access_token)
            EventBus.$emit('tokenSet', response.data.access_token)
            axios.defaults.headers.common.Authorization = 'Bearer ' + response.data.access_token
            this.getProjects()
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    }
    EventBus.$on('tokenSet', (token) => { this.cookieToken = token })
  },

  methods: {
    getProjects () {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/data/projects/')
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.projects = response.data
            if (this.$cookies.get('last_used_project_id') !== null) {
              if (this.$route.params.id !== undefined) {
                const matchingProject = this.projects.filter(project => project.id === Number(this.$route.params.id))
                if (matchingProject.length > 0) {
                  this.selectedProject = this.projects.filter(project => project.id === Number(this.$route.params.id))[0]
                  this.getProjectData(this.selectedProject.id)
                } else {
                  this.isLoadingAlerts = false
                  this.isLoadingFeeds = false
                  this.isLoadingLastUpdate = false
                  this.isLoadingProducts = false
                  this.isLoadingSources = false
                  this.$router.push({ name: 'not-found' })
                }
              } else if (this.$route.params.projectId !== undefined) {
                const matchingProject = this.projects.filter(project => project.id === Number(this.$route.params.projectId))
                if (matchingProject.length > 0) {
                  this.selectedProject = this.projects.filter(project => project.id === Number(this.$route.params.projectId))[0]
                  this.getProjectData(this.selectedProject.id)
                } else {
                  this.isLoadingAlerts = false
                  this.isLoadingFeeds = false
                  this.isLoadingLastUpdate = false
                  this.isLoadingProducts = false
                  this.isLoadingSources = false
                  this.$router.push({ name: 'not-found' })
                }
              } else {
                this.selectedProject = this.projects.filter(project => project.id === Number(this.$cookies.get('last_used_project_id')))[0]
                this.getProjectData(this.selectedProject.id)
              }
            } else if (this.projects.length === 1) {
              this.selectedProject = this.projects[0]
              this.getProjectData(this.selectedProject.id)
            } else {
              this.isLoadingAlerts = false
              this.isLoadingFeeds = false
              this.isLoadingLastUpdate = false
              this.isLoadingProducts = false
              this.isLoadingSources = false
            }
            this.generateSidebarMenu()
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    selectProject (projectId) {
      this.selectedProject = this.projects.filter(project => project.id === projectId)[0]
      this.getProjectData(projectId)
      this.$cookies.set('last_used_project_id', projectId)
      this.generateSidebarMenu()
    },

    activateAccount () {
      this.isLoadingAlerts = false
      this.isLoadingFeeds = false
      this.isLoadingLastUpdate = false
      this.isLoadingProducts = false
      this.isLoadingSources = false
      this.generateSidebarMenu()
    },

    createProject () {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/auth/refresh')
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.$cookies.set('access_token', response.data.access_token)
            EventBus.$emit('tokenSet', response.data.access_token)
            axios.defaults.headers.common.Authorization = 'Bearer ' + response.data.access_token
            this.getProjects()
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    generateSidebarMenu () {
      this.menu = [{ header: false, hiddenOnCollapse: false }]
      this.menu.push({ href: '/projects/', title: this.$t('global.projects') })
      if (Object.keys(this.selectedProject).length !== 0) {
        this.menu.push(
          {
            class: 'mx-4',
            href: '/project/' + this.selectedProject.id,
            title: this.selectedProject.name,
            child: [
              { class: 'mx-3', href: '/project/' + this.selectedProject.id + '/sources', title: this.$t('global.sources'), icon: { element: 'img', attributes: { src: '/app/images/icons/menu/sources.svg' } } },
              { class: 'mx-3', href: '/project/' + this.selectedProject.id + '/products', title: this.$t('global.products_menu'), icon: { element: 'img', attributes: { src: '/app/images/icons/menu/products.svg' } } },
              { class: 'mx-3', href: '/project/' + this.selectedProject.id + '/feeds', title: this.$t('global.feeds'), icon: { element: 'img', attributes: { src: '/app/images/icons/menu/feeds.svg' } } }
            ]
          })
      }
      this.menu.push({ href: '/settings/', title: this.$t('global.settings_upper') })
      this.menu.push({ href: '/logout/', title: this.$t('global.logout') })
    },

    updateUserData (cookieToken) {
      const decoded = jwtDecode(cookieToken)
      if (decoded.userData !== null) {
        window.dataLayer.push({ userId: decoded.id, companyName: decoded.userData.company, userInitials: decoded.userData.name.substr(0, 1) + decoded.userData.surname.substr(0, 1) })
        this.userName = decoded.userData.name
        this.userSurname = decoded.userData.surname
        this.planLevel = decoded.userData.planLevel
      } else {
        this.userName = ''
        this.userSurname = ''
        this.planLevel = ''
      }
      this.$cookies.set('language_version', decoded.language)
      this.$i18n.locale = decoded.language
    }
  },

  computed: {
    isLoading () {
      if (this.isLoadingSources || this.isLoadingFeeds || this.isLoadingAlerts || this.isLoadingLastUpdate || this.isLoadingProducts) return true
      else return false
    }
  },

  watch: {
    cookieToken: function (newCookieToken) {
      this.updateUserData(newCookieToken)
      this.selectedProject = {}
    }
  },

  components: { SearchIcon }
}
</script>
